import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import type { ProjectScopeView, ScopeCoverage } from '@one-deployment/api'
import { Badge, Button, Card, Input, Label, PageHeader, Segmented, SkeletonRows, Textarea } from '@/components/ui'
import { EmptyState, ErrorState } from '@/components/status'
import { RoleGate } from '@/components/RoleGate'
import { ApiError, enc, get, post } from '@/lib/api'
import { dateTime } from '@/lib/format'

const errText = (e: unknown) => (e instanceof ApiError ? (e.what ?? e.message) : String(e))
const PASS: Record<string, string> = { points: 'Points', workflows: 'Workflows', follow_ups: 'Follow-ups', bug_hunt: 'Bug hunts', ui: 'UI' }

/** The approved detailed scope, the tickets made from it, and the scope check (features/05, 06). */
export function DetailedScopePage() {
  const { project } = useParams({ strict: false }) as { project: string }
  const qc = useQueryClient()
  const key = ['project-scope', project] as const
  const scope = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => get<ProjectScopeView>(`/api/projects/${enc(project)}/scope`, signal),
    refetchInterval: (q) => (q.state.data?.runs.some((r) => r.status === 'creating' || r.status === 'splitting') ? 5000 : false),
  })
  const cov = useQuery({ queryKey: [...key, 'coverage'], queryFn: ({ signal }) => get<ScopeCoverage>(`/api/projects/${enc(project)}/scope/coverage`, signal), refetchInterval: 30_000 })
  const [tab, setTab] = useState<'coverage' | 'approve' | 'text'>('coverage')
  const tickets = useMutation({
    mutationFn: (stage: 'mvp' | 'backend') => post<ProjectScopeView>(`/api/projects/${enc(project)}/scope/tickets`, { stage }),
    onSuccess: (v) => qc.setQueryData(key, v),
  })
  const v = scope.data
  const c = cov.data

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Detailed scope"
        meta={v?.current ? <>Approved {dateTime(v.current.approvedAt)} by {v.current.approvedBy} · {v.current.points} points{v.current.sourceUrl && <> · <a href={v.current.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">live doc <ExternalLink size={11} /></a></>}</> : 'Not approved yet'}
        actions={<Segmented value={tab} onChange={setTab} options={[{ value: 'coverage', label: 'Scope check' }, { value: 'text', label: 'Approved copy' }, { value: 'approve', label: 'Approve' }]} />}
      />
      <div className="min-h-0 flex-1 overflow-auto p-5">
        {scope.error ? <ErrorState error={scope.error} retry={() => void scope.refetch()} /> : !v ? <SkeletonRows rows={6} /> : (
          <div className="flex flex-col gap-4">
            {tab === 'approve' && <Approve project={project} onDone={() => { setTab('coverage'); void qc.invalidateQueries({ queryKey: key }) }} />}
            {tab === 'text' && (v.current ? (
              <Card title="Approved copy"><pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap p-3 font-mono text-[11px]">{v.current.body}</pre></Card>
            ) : <EmptyState title="Nothing approved yet" body="Approve the detailed scope once the client agrees." />)}
            {tab === 'coverage' && !v.current && <EmptyState title="Nothing approved yet" body="When the client agrees the detailed scope, an admin approves it here. Tickets and the scope check come from that copy." />}
            {tab === 'coverage' && v.current && (
              <>
                <Card title="Tickets from the scope">
                  <div className="flex flex-col gap-2 p-3 text-sm">
                    <p className="text-fg-subtle">Claude splits the approved scope in five passes: one ticket per point, then workflows end to end, follow-ups, bug hunts per workflow, and UI. They go into Linear, triaged, and wait to be started.</p>
                    <RoleGate action="scope.tickets" project={project}>
                      <div className="flex gap-2">
                        <Button variant="primary" disabled={tickets.isPending} onClick={() => tickets.mutate('mvp')}>Make MVP tickets (frontend)</Button>
                        <Button disabled={tickets.isPending} onClick={() => tickets.mutate('backend')}>Make backend tickets</Button>
                      </div>
                    </RoleGate>
                    {tickets.error && <div role="alert" className="text-xs text-err">{errText(tickets.error)}</div>}
                    {v.runs.length > 0 && (
                      <ul className="mt-1 text-xs">
                        {v.runs.map((r) => {
                          const [stage, pass] = (r.pass ?? ':').split(':')
                          return (
                            <li key={r.id} className="flex items-center gap-2 border-t border-border py-1">
                              <span className="w-16 text-fg-subtle">{stage === 'mvp' ? 'MVP' : 'Backend'}</span>
                              <span className="w-24">{PASS[pass ?? ''] ?? pass}</span>
                              <span className="flex-1 truncate text-fg-subtle">{r.summary ?? r.title.replace(/^Scope tickets: /, '')}</span>
                              <Badge tone={r.status === 'done' ? 'ok' : r.status === 'failed' ? 'warn' : 'info'}>{r.status}</Badge>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                </Card>
                <Card title={c ? `Scope check: ${c.done} of ${c.points} points done · ${c.points - c.covered} with no ticket` : 'Scope check'}>
                  {cov.error ? <ErrorState error={cov.error} retry={() => void cov.refetch()} /> : !c ? <SkeletonRows rows={8} /> : (
                    <ul className="text-xs">
                      {c.lines.map((l) => (
                        <li key={l.n} className={`flex gap-2 border-b border-border px-3 py-1 last:border-0 ${l.heading ? 'bg-surface-2 font-semibold' : ''}`}>
                          <span className="w-10 shrink-0 font-mono text-fg-faint">L{l.n}</span>
                          <span className="flex-1">{l.text}</span>
                          {!l.heading && (l.tickets.length === 0 ? <Badge tone="warn">no ticket</Badge> : (
                            <span className="flex flex-wrap justify-end gap-1">
                              {l.tickets.map((t) => (
                                <a key={t.identifier} href={t.url} target="_blank" rel="noreferrer" title={`${t.title} · ${t.state}`}>
                                  <Badge tone={t.done ? 'ok' : 'muted'}>{t.identifier}</Badge>
                                </a>
                              ))}
                            </span>
                          ))}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </>
            )}
            {v.history.length > 1 && tab !== 'coverage' && (
              <Card title="Earlier approvals">
                <ul className="text-xs">{v.history.slice(1).map((h) => <li key={h.id} className="border-b border-border px-3 py-1 last:border-0">{dateTime(h.approvedAt)} · {h.approvedBy} · {h.points} points</li>)}</ul>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function Approve({ project, onDone }: { project: string; onDone: () => void }) {
  const [text, setText] = useState('')
  const [url, setUrl] = useState('')
  const m = useMutation({
    mutationFn: () => post<ProjectScopeView>(`/api/projects/${enc(project)}/scope`, { text, ...(url.trim() ? { sourceUrl: url.trim() } : {}) }),
    onSuccess: () => { setText(''); onDone() },
  })
  return (
    <RoleGate action="scope.approve" project={project} fallback={<EmptyState title="Admins approve the scope" body="Ask an admin to approve it once the client agrees." />}>
      <Card title="Approve the detailed scope">
        <div className="flex flex-col gap-3 p-3 text-sm">
          <p className="text-fg-subtle">Do this when the client has agreed it. A frozen copy is kept; triage and the scope check use that copy, not the live doc. Approving again makes a new copy and keeps the old ones.</p>
          <div><Label htmlFor="ds-url">Google Doc link (shared as "anyone with the link can view")</Label><Input id="ds-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.google.com/document/d/…" /></div>
          <div><Label htmlFor="ds-text">Or paste the scope text (used instead of the doc when filled)</Label><Textarea id="ds-text" rows={12} value={text} onChange={(e) => setText(e.target.value)} /></div>
          {m.error && <div role="alert" className="text-xs text-err">{errText(m.error)}</div>}
          <div><Button variant="primary" disabled={m.isPending || (!text.trim() && !url.trim())} onClick={() => m.mutate()}>{m.isPending ? 'Approving…' : 'Approve scope'}</Button></div>
        </div>
      </Card>
    </RoleGate>
  )
}
